import React, { useState } from 'react';
import { format } from 'date-fns';
import { useDroppable } from '@dnd-kit/core';
import { formatCustomDate } from '../../../utils/dateFormatters';
import {
  DayContainer,
  DayColumnHeader,
  DayColumnHeaderContent,
  DayLabel,
  ExpectedHours,
  IsTodayLabel,
  DayColumnBody,
  DayHeaderNavButton,
  DayColumnFooter,
  DayColumnItemCount,
  DayColumnFooterButton,
} from '../styled';
import { DraggableEvent } from './DraggableEvent';
import { OccurrenceDraggableEvent } from './OccurrenceDraggableEvent';
import { ArrowLeftIcon } from './ArrowLeftIcon';
import { ArrowRightIcon } from './ArrowRightIcon';
import { CalendarEvent, EventColorSet } from 'pages/Agenda/utils';
import { useDayColumn } from '../../../hooks/useDayColumn';
import { ReactComponent as ChevronIcon } from 'assets/icons/navigation/small/chevron-right-sm.svg';

interface DayColumnProps {
  date: Date;
  events: CalendarEvent[];
  horasPrevistasLabel: string;
  dayIndex: number;
  onCardHover: (data: { event: CalendarEvent; rect: DOMRect; colorSet: EventColorSet }) => void;
  onCardLeave: () => void;
  onCardClick: (event: CalendarEvent, colorSet: EventColorSet) => void;
  isDragging: boolean;
  getColorSet: (event: CalendarEvent) => EventColorSet;
  isCompletedView: boolean;
  onNavigateByDay?: (direction: 'prev' | 'next') => void;
  maxCards?: number;
  compact?: boolean;
}

export const DayColumn: React.FC<DayColumnProps> = ({
  date,
  events,
  horasPrevistasLabel,
  dayIndex,
  onCardHover,
  onCardLeave,
  onCardClick,
  isDragging,
  getColorSet,
  isCompletedView,
  onNavigateByDay,
  maxCards,
  compact,
}) => {
  const [page, setPage] = useState(0);
  const { setNodeRef, isOver } = useDroppable({
    id: format(date, 'yyyy-MM-dd'),
  });

  const { isToday, isTomorrowDate, isWeekendDay, showLeftArrow, showRightArrow } = useDayColumn({
    date,
    dayIndex,
    onNavigateByDay,
  });

  const hasOverflow = maxCards !== undefined && events.length > maxCards;
  const totalPages = maxCards ? Math.ceil(events.length / maxCards) : 1;
  const safePage = Math.min(page, totalPages - 1);
  const visibleEvents = hasOverflow && maxCards ? events.slice(safePage * maxCards, (safePage + 1) * maxCards) : events;

  return (
    <DayContainer
      ref={setNodeRef}
      isFirst={dayIndex === 0}
      isLast={dayIndex === 6}
      isHighlighted={isToday}
      removeLeftBorder={isTomorrowDate}
      isWeekend={isWeekendDay}
      isDropTarget={isOver}
    >
      {isToday && <IsTodayLabel $hasLeftArrow={showLeftArrow}>Hoje</IsTodayLabel>}
      <DayColumnHeader $hasLeftArrow={showLeftArrow} $hasRightArrow={showRightArrow}>
        {showLeftArrow && (
          <DayHeaderNavButton
            type="button"
            side="left"
            onClick={() => onNavigateByDay?.('prev')}
            aria-label="Voltar um dia"
          >
            <ArrowLeftIcon />
          </DayHeaderNavButton>
        )}
        <DayColumnHeaderContent $grow={showLeftArrow || showRightArrow}>
          <DayLabel isHighlighted={isToday} isBlack={date.getTime() > new Date().getTime()}>
            {formatCustomDate(date)}
          </DayLabel>
          <ExpectedHours>{horasPrevistasLabel} previstas</ExpectedHours>
        </DayColumnHeaderContent>
        {showRightArrow && (
          <DayHeaderNavButton
            type="button"
            side="right"
            onClick={() => onNavigateByDay?.('next')}
            aria-label="Avançar um dia"
          >
            <ArrowRightIcon />
          </DayHeaderNavButton>
        )}
      </DayColumnHeader>
      <DayColumnBody>
        {visibleEvents.map(event =>
          event.eventKind === 'ocorrencia' ? (
            <OccurrenceDraggableEvent
              key={event.id}
              event={event}
              onHover={onCardHover}
              onLeave={onCardLeave}
              onCardClick={onCardClick}
              isDragging={isDragging}
              colorSet={getColorSet(event)}
              isCompletedView={isCompletedView}
              compact={compact}
            />
          ) : (
            <DraggableEvent
              key={event.id}
              event={event}
              onHover={onCardHover}
              onLeave={onCardLeave}
              onCardClick={onCardClick}
              isDragging={isDragging}
              colorSet={getColorSet(event)}
              isCompletedView={isCompletedView}
              compact={compact}
            />
          ),
        )}
      </DayColumnBody>
      {hasOverflow && (
        <DayColumnFooter>
          <DayColumnItemCount>{events.length} itens</DayColumnItemCount>
          <DayColumnFooterButton
            type="button"
            onClick={() => setPage(p => Math.max(0, p - 1))}
            aria-label="Página anterior"
            disabled={safePage === 0}
            $rotate={-90}
          >
            <ChevronIcon />
          </DayColumnFooterButton>
          <DayColumnFooterButton
            type="button"
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            aria-label="Próxima página"
            disabled={safePage >= totalPages - 1}
            $rotate={90}
          >
            <ChevronIcon />
          </DayColumnFooterButton>
        </DayColumnFooter>
      )}
    </DayContainer>
  );
};
